/**
 * product-type.ts
 * ─────────────────────────────────────────────────────────────
 * Shared helpers for Normal vs Dropshipping product separation.
 *
 * SOURCE OF TRUTH: product.is_dropshipping
 * product_type (simple/variable/etc.) is a CATEGORY field — never use it for business logic.
 */

// ─── Types ───────────────────────────────────────────────────

/** Minimal shape needed to determine business model */
interface ProductLike {
  is_dropshipping?: boolean | null
}

/** Canonical business model string */
export type BusinessModel = 'dropshipping' | 'normal'

// ─── Business Model Helpers ───────────────────────────────────

/**
 * Returns true if the product is a dropshipping product.
 * Falls back to false if the field is missing (backward compat).
 */
export function isDropshipping(product: ProductLike | null | undefined): boolean {
  return Boolean(product?.is_dropshipping)
}

/**
 * Returns true if the product is a normal (stock-based) product.
 */
export function isNormal(product: ProductLike | null | undefined): boolean {
  return !isDropshipping(product)
}

/**
 * Returns the canonical business model string.
 * Infers from is_dropshipping — backward compatible.
 */
export function getBusinessModel(product: ProductLike | null | undefined): BusinessModel {
  return isDropshipping(product) ? 'dropshipping' : 'normal'
}

// ─── Validation ───────────────────────────────────────────────

function isAliExpressHostname(hostname: string): boolean {
  const h = hostname.toLowerCase().replace(/^www\./, "")
  return h === "aliexpress.com" || h.endsWith(".aliexpress.com")
}

/**
 * Validates a supplier URL for dropshipping import.
 * - Must not be empty
 * - Must be a valid URL (starts with http:// or https://)
 * - Hostname must be aliexpress.com or a regional subdomain (e.g. fr.aliexpress.com)
 *
 * Returns an error string if invalid, null if valid.
 */
export function validateSupplierUrl(url: string | null | undefined): string | null {
  const trimmed = (url ?? '').trim()

  if (!trimmed) {
    return 'Le lien fournisseur est requis pour importer un produit.'
  }

  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return 'Le lien doit commencer par http:// ou https://.'
  }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return 'Le lien fournisseur n\'est pas une URL valide.'
  }

  if (!isAliExpressHostname(parsed.hostname)) {
    // Soft warning — we log but do NOT hard-reject non-AliExpress in UI.
    // Callers can choose to warn vs reject based on context.
    return `Attention : ce lien ne semble pas venir d'AliExpress (${parsed.hostname}). Vérifiez que c'est le bon lien fournisseur.`
  }

  return null
}

/**
 * Strict version — hard-rejects ANY URL that is not from AliExpress.
 * Use this for import validation where AliExpress is mandatory.
 */
export function validateSupplierUrlStrict(url: string | null | undefined): string | null {
  const trimmed = (url ?? '').trim()

  if (!trimmed) {
    return 'Le lien fournisseur est requis pour importer un produit.'
  }

  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return 'Le lien doit commencer par http:// ou https://.'
  }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return 'Le lien fournisseur n\'est pas une URL valide.'
  }

  if (!isAliExpressHostname(parsed.hostname)) {
    return `Ce lien n'est pas un lien AliExpress valide. Seuls les domaines aliexpress.com sont acceptés.`
  }

  return null
}

// ─── Price Validation ─────────────────────────────────────────

/**
 * Validates dropshipping prices.
 * - Both must be finite numbers > 0
 * - No NaN, no Infinity
 *
 * Returns an error string if invalid, null if valid.
 */
export function validateDropshipPrices(
  supplierPrice: number | null | undefined,
  sellingPrice: number | null | undefined
): string | null {
  const sp = Number(supplierPrice)
  const se = Number(sellingPrice)

  if (!Number.isFinite(sp) || sp <= 0) {
    return 'Le prix fournisseur doit être un nombre positif supérieur à 0.'
  }

  if (!Number.isFinite(se) || se <= 0) {
    return 'Le prix de vente doit être un nombre positif supérieur à 0.'
  }

  return null
}

// ─── Profit / Margin ──────────────────────────────────────────

/**
 * Calculate profit and margin from selling price and supplier price.
 * Returns null values if inputs are invalid.
 */
export function calculateProfit(
  sellingPrice: number | null | undefined,
  supplierPrice: number | null | undefined
): { profit: number | null; marginPercent: number | null } {
  const sell = Number(sellingPrice)
  const buy = Number(supplierPrice)

  if (!Number.isFinite(sell) || !Number.isFinite(buy) || buy <= 0) {
    return { profit: null, marginPercent: null }
  }

  const profit = sell - buy
  const marginPercent = ((sell - buy) / buy) * 100

  return {
    profit: Math.round(profit * 1000) / 1000,
    marginPercent: Math.round(marginPercent * 10) / 10,
  }
}

// ─── Safe Variant ─────────────────────────────────────────────

interface VariantLike {
  id: string
  price: number | null
}

interface ProductWithVariant {
  id: string
  is_dropshipping?: boolean | null
  default_variant_id?: string | null
  price?: number
}

interface OrderItemLike {
  variant_id?: string | null
  product_id: string
}

/**
 * Returns the safe variant ID for an order item.
 * For dropshipping: use default_variant_id (always exists post-fix).
 * For normal: require item.variant_id.
 *
 * Throws if no safe variant is found — prevents silent order corruption.
 */
export function getSafeVariantId(
  product: ProductWithVariant,
  item: OrderItemLike,
  variants: VariantLike[]
): string {
  // If item has an explicit variant and it exists in the list — use it
  if (item.variant_id) {
    const found = variants.find(v => v.id === item.variant_id)
    if (found) return found.id
  }

  // Fallback: product's default_variant_id
  if (product.default_variant_id) {
    const found = variants.find(v => v.id === product.default_variant_id)
    if (found) return found.id
  }

  // Last resort: first variant
  if (variants.length > 0) {
    return variants[0].id
  }

  throw new Error(
    `[getSafeVariantId] No variant found for product ${product.id}. Cart/order cannot proceed.`
  )
}

// ─── Stock Guards ─────────────────────────────────────────────

/**
 * Asserts that stock logic must NOT run for a dropshipping product.
 * Call this at the top of any stock-mutation function.
 *
 * Usage:
 *   if (assertNotDropshipping(product)) return;
 *
 * Returns true if the product is dropshipping (caller should skip stock logic).
 */
export function guardStockForNormalOnly(product: ProductLike | null | undefined): boolean {
  if (isDropshipping(product)) {
    console.warn(
      '[STOCK GUARD] Attempted stock operation on dropshipping product — skipped.',
      product
    )
    return true // caller should return early
  }
  return false
}
